import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Archive, ArchiveRestore, ArrowDown, ArrowUp, BookUser, Copy, EyeOff, FileDown, History, ListChecks, Loader2,
  Lock, Maximize2, MessageSquare, Plus, Presentation, Redo2, Settings2, SlidersHorizontal, Sparkles, Trash2, Undo2, X,
} from "lucide-react";
import { offerApi } from "../../../services/offerApi";
import { presentationApi } from "../../../services/presentationApi";
import { DriveChip, GuidesPanel, PresentationSettings, useDriveStatus } from "../../Presentations/PresentationPanels";
import { openNexus, usePublishNexusPage } from "../../../context/nexusPage";
import { useAuth } from "../../Auth";
import { useVersionedDocument } from "../useVersionedDocument";
import { BackLink, SaveStatus, SyncBanners, TitleField, ToolbarButton } from "../EditorChrome";
import RevisionsPanel from "../RevisionsPanel";
import useClientShare from "../clientShare";
import ClientProfilePanel from "../ClientProfilePanel";
import ScaledSlide from "./ScaledSlide";
import SlideCanvas from "./SlideCanvas";
import SlideInspector from "./SlideInspector";
import SlideViewer from "./SlideViewer";
import ProposalSettings from "./ProposalSettings";
import CommentsPanel, { useProposalComments } from "./CommentsPanel";
import { diffProposal, mergeChanges } from "./changes";
import {
  addBlock, duplicateBlock, duplicateSlide, insertSlide, layoutsFor, moveBlock, moveSlide, newSlide, removeBlock,
  removeSlide, updateBlock, updateMeta, updateSlide,
} from "./proposalOps";

// Durée de la surbrillance d'un bloc modifié, une fois la slide affichée.
const HIGHLIGHT_MS = 8000;

const PROPOSAL_API = {
  get: offerApi.proposal,
  save: offerApi.saveProposal,
  restore: offerApi.restoreProposal,
  revisions: offerApi.proposalRevisions,
  label: offerApi.labelProposal,
  updateMeta: offerApi.updateProposal,
};

// Le même éditeur sert aux présentations client (rattachées à un projet, un client ou un programme) : pas de
// chiffrage ni de fiche offres, mais le PDF tenu à jour sur le Drive et les consignes tenues par l'IA.
const KINDS = {
  proposal: { api: PROPOSAL_API, docKey: "proposal", comments: offerApi, noun: "Proposition", document: "proposition",
              back: "/tools/offres?onglet=propositions", backLabel: "Propositions" },
  presentation: { api: presentationApi, docKey: "presentation", comments: presentationApi, noun: "Présentation",
                  document: "presentation", back: "/tools/presentations", backLabel: "Présentations" },
};

const scopeLink = (scope) => (scope?.type === "project" ? `/project/${scope.id}` : null);

// Numéro de page de chaque slide visible ; les slides masquées n'en ont pas.
const pageNumbers = (slides) => {
  let page = 0;
  return Object.fromEntries(slides.map((slide) => [slide.id, slide.hidden ? null : (page += 1)]));
};

const SlideButton = ({ title, onClick, children, danger, disabled }) => (
  <button type="button" title={title} aria-label={title} disabled={disabled} onClick={onClick}
    className={`rounded-md p-1.5 text-slate-500 hover:bg-slate-100 disabled:opacity-30 ${danger ? "hover:text-red-600" : "hover:text-slate-900"}`}>
    {children}
  </button>
);

// Sur grand écran, l'espace de travail prend la hauteur de la fenêtre sous l'en-tête : la slide s'y agrandit
// autant qu'elle peut, la liste des slides et le panneau latéral défilent à sa hauteur.
const WIDE = "(min-width: 1280px)";
const useWorkspaceHeight = (ref) => {
  const [height, setHeight] = useState(null);
  useLayoutEffect(() => {
    const update = () => {
      if (!ref.current || !window.matchMedia(WIDE).matches) { setHeight(null); return; }
      const top = ref.current.getBoundingClientRect().top + window.scrollY;
      setHeight(Math.max(480, Math.round(window.innerHeight - top - 16)));
    };
    update();
    window.addEventListener("resize", update);
    // Une bannière qui apparaît au-dessus décale l'espace de travail.
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(update);
    observer?.observe(document.body);
    return () => { window.removeEventListener("resize", update); observer?.disconnect(); };
  });
  return height;
};

const ProposalEditor = ({ kind = "proposal" }) => {
  const { proposalId } = useParams();
  const id = Number(proposalId);
  const config = KINDS[kind];
  const isDeck = kind === "presentation";
  // Les commentaires à droite par défaut ; le réglage des blocs s'ouvre avec « Édition ».
  const [panel, setPanel] = useState("comments");
  const [enlarged, setEnlarged] = useState(false);
  const workspaceRef = useRef(null);
  const workspaceHeight = useWorkspaceHeight(workspaceRef);
  const [selectedSlideId, setSelectedSlideId] = useState(null);
  const [selectedBlockId, setSelectedBlockId] = useState(null);
  const [addingSlide, setAddingSlide] = useState(false);
  const doc = useVersionedDocument({ id, api: config.api, docKey: config.docKey, watchRevisions: panel === "revisions" });
  const {
    data, setData, loadError, shown, apply, undo, redo, canUndo, canRedo, setMessage, save, canEdit, archived,
    editable, viewing, viewRevision, restoreRevision, labelRevision, revisions, updateMeta: updateDocument,
    remoteChange,
  } = doc;
  // Modifications arrivées d'ailleurs (Nexus, collègue), pas encore vues slide par slide.
  const [changes, setChanges] = useState({});
  const [notice, setNotice] = useState(null);
  const [exporting, setExporting] = useState(false); // PDF en préparation sur le serveur

  useEffect(() => {
    if (!remoteChange) return;
    const found = diffProposal(remoteChange.previous, remoteChange.next);
    const count = Object.keys(found).length;
    if (!count) return;
    setChanges((current) => mergeChanges(current, found));
    const revision = remoteChange.revision || {};
    setNotice({ who: revision.source === "agent" ? "Nexus" : (revision.author_name || "Quelqu'un"), count,
                summary: revision.summary || "", at: remoteChange.at });
  }, [remoteChange]);

  const { user } = useAuth();
  const clientShare = useClientShare({ data, setData, user, refreshRevisions: doc.refreshRevisions, setMessage, revisions });
  const { comments, refresh: refreshComments } = useProposalComments(id, setMessage, config.comments);
  const drive = useDriveStatus(isDeck ? id : null);
  const slides = useMemo(() => shown?.slides || [], [shown]);
  const pages = useMemo(() => pageNumbers(slides), [slides]);
  const slide = slides.find((item) => item.id === selectedSlideId) || slides[0];
  const block = slide?.blocks?.find((item) => item.id === selectedBlockId) || null;
  const currentChange = slide && !viewing ? changes[slide.id] : null;

  // Une slide modifiée s'illumine quand on la regarde, puis la marque disparaît.
  const currentChangeKey = currentChange ? JSON.stringify(currentChange) : "";
  useEffect(() => {
    if (!currentChangeKey || !slide) return undefined;
    const seen = slide.id;
    const timer = setTimeout(() => setChanges(({ [seen]: _done, ...rest }) => rest), HIGHLIGHT_MS);
    return () => clearTimeout(timer);
  }, [currentChangeKey, slide]);
  // La liste des slides défile à part sur grand écran : la vignette de la slide ouverte y reste visible.
  const navRef = useRef(null);
  const currentSlideId = slide?.id;
  useEffect(() => {
    navRef.current?.querySelector('[aria-current="true"]')?.scrollIntoView({ block: "nearest", inline: "nearest" });
  }, [currentSlideId]);
  const estimate = viewing ? viewing.estimate : data?.estimate;
  const lots = estimate?.lots || [];
  const openComments = comments.filter((comment) => comment.status === "open");
  const openBySlide = openComments.reduce((acc, comment) => ({ ...acc, [comment.slide_id]: (acc[comment.slide_id] || 0) + 1 }), {});

  // Ce que l'utilisateur regarde, pour Nexus : « cette slide », « ce bloc » deviennent des identifiants.
  const title = data?.[config.docKey]?.title;
  usePublishNexusPage(data && slide ? {
    description: `${config.noun} « ${title} », slide ${pages[slide.id] ?? "masquée"} sur ${slides.filter((item) => !item.hidden).length}`
      + (slide.title ? ` « ${slide.title} »` : ""),
    extra: {
      ...(isDeck ? {
        deck_document: "présentation client (outils presentations__*)",
        presentation_id: id,
        presentation_title: title,
        rattachement: data.presentation.scope?.label,
      } : {
        offer_document: "proposition",
        proposal_id: id,
        proposal_title: title,
        estimate_id: data.proposal.estimate_id,
      }),
      slide_id: slide.id,
      slide_number: pages[slide.id] ?? null,
      slide_title: slide.title || "",
      selected_block_id: block?.id || null,
      selected_block_type: block?.type || null,
      open_comments: openComments.length,
      consulted_version: viewing ? viewing.revision.number : null,
      can_edit: editable,
    },
  } : null);

  if (loadError) {
    return (
      <div className="mx-auto max-w-3xl space-y-4 py-10 text-center">
        <p className="text-slate-700">{loadError}</p>
        <Link to={config.back} className="text-sm text-blue-700 hover:underline">Retour aux {config.backLabel.toLowerCase()}</Link>
      </div>
    );
  }
  if (!data || !shown) return <div className="py-10 text-center text-sm text-slate-500">Chargement…</div>;

  const proposal = data[config.docKey];
  const clientProfile = isDeck ? null : data.client_profile;
  const affaire = proposal.affaire || {};
  const scope = proposal.scope || null;
  const embeds = (viewing || data).embeds || {};
  const visibleSlides = slides.filter((item) => !item.hidden);
  const slideId = slide?.id;
  const slideIndex = slides.findIndex((item) => item.id === slideId);
  // La slide ouverte, modifiable sur place ; le cadre de débordement ne sert que dans l'éditeur.
  const canvas = (inEditor) => (
    <SlideCanvas slide={slide} meta={shown.meta} estimate={estimate} page={pages[slide.id]}
      edit={editable}
      selectedBlockId={selectedBlockId} onSelectBlock={setSelectedBlockId} showOverflow={inEditor}
      highlight={inEditor ? currentChange : null} embeds={embeds} live deckTitle={proposal.title}
      onSlide={onSlide} onBlock={onBlock} onMeta={onMeta} />
  );

  const onSlide = (patch, key) => apply((current) => updateSlide(current, slideId, patch), key && `${slideId}.${key}`);
  const onBlock = (blockId, patch, key) => apply((current) => updateBlock(current, slideId, blockId, patch), key);
  const onMeta = (patch, key) => apply((current) => updateMeta(current, patch), key);
  const selectSlide = (nextId) => { setSelectedSlideId(nextId); setSelectedBlockId(null); };

  const addSlide = (layout) => {
    const created = newSlide(layout);
    apply((current) => insertSlide(current, slideId, created));
    selectSlide(created.id);
    setAddingSlide(false);
  };

  const deleteSlide = (target) => {
    if (!window.confirm("Supprimer cette slide ? (Ctrl+Z pour revenir en arrière)")) return;
    const index = slides.findIndex((item) => item.id === target);
    apply((current) => removeSlide(current, target));
    selectSlide(slides[index + 1]?.id || slides[index - 1]?.id || null);
  };

  const saveTemplate = async (clear) => {
    const question = clear
      ? "Retirer le modèle de ce client ?"
      : `Enregistrer cette proposition comme modèle pour ${clientProfile?.client_name} ? Il remplacera le modèle actuel.`;
    if (!window.confirm(question)) return;
    try {
      await save();
      const profile = await offerApi.saveClientTemplate(id, clear);
      setData((previous) => ({ ...previous, client_profile: profile }));
    } catch (error) {
      setMessage(error.message);
    }
  };

  // Le logo de la couverture devient celui du client : repris par ses prochaines propositions.
  const saveClientLogo = async (logo) => {
    try {
      const profile = await offerApi.updateClientProfile(clientProfile.client_id, { logo });
      setData((previous) => ({ ...previous, client_profile: { ...previous.client_profile, ...profile } }));
    } catch (error) {
      setMessage(error.message);
    }
  };

  const askNexusForComments = () => openNexus(
    `Applique ${openComments.length > 1 ? `les ${openComments.length} commentaires ouverts` : "le commentaire ouvert"} de la ${config.noun.toLowerCase()} « ${proposal.title} », `
    + `puis marque-${openComments.length > 1 ? "les" : "le"} comme traité${openComments.length > 1 ? "s" : ""} en disant ce que tu as modifié.`,
  );

  const askNexusForSlide = () => openNexus(
    `Sur la slide ${pages[slideId] ?? ""}${slide?.title ? ` « ${slide.title} »` : ""}${block ? ", bloc sélectionné" : ""} : `,
  );

  const goTo = (targetSlideId, targetBlockId) => {
    setSelectedSlideId(targetSlideId);
    setSelectedBlockId(targetBlockId || null);
  };

  // PDF imprimé par le serveur : la version exportée est retenue (Nexus la reconnaît jointe à un mail envoyé) et
  // figée. Serveur sans impression (503) : la version imprimable du navigateur, comme avant.
  const exportPdf = async () => {
    setExporting(true);
    try {
      await save();
      const { blob, filename } = await (isDeck ? presentationApi.pdf : offerApi.proposalPdf)(id, viewing?.revision.id);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (error) {
      if (error.status === 503 && !isDeck) {
        const revision = viewing ? `?revision=${viewing.revision.id}` : "";
        window.open(`/tools/offres/propositions/${id}/imprimer${revision}`, "_blank", "noopener");
      } else {
        setMessage(error.message);
      }
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-4 py-6">
      <div className="space-y-2">
        <BackLink to={config.back} label={config.backLabel} />
        {/* Présentation : deux boutons de plus (Consignes, Présenter), le titre garde sa ligne jusqu'aux très grands écrans. */}
        <div className={`flex flex-col gap-3 ${isDeck ? "min-[1700px]:flex-row min-[1700px]:items-start min-[1700px]:justify-between"
          : "xl:flex-row xl:items-start xl:justify-between"}`}>
          <div className="min-w-0 flex-1 space-y-1">
            <TitleField value={proposal.title} editable={canEdit} label={`Titre de la ${config.noun.toLowerCase()}`}
              onCommit={(title) => updateDocument({ title })} />
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-1 text-sm text-slate-500">
              {isDeck ? (
                <>
                  <span className="font-medium text-slate-700">
                    {{ project: "Projet", client: "Client", programme: "Programme" }[scope?.type]}
                  </span>
                  {scopeLink(scope)
                    ? <Link to={scopeLink(scope)} className="hover:text-slate-800 hover:underline">{scope.label}</Link>
                    : <span>{scope?.label}</span>}
                  <DriveChip drive={drive} onOpen={() => setPanel("settings")} />
                </>
              ) : (
                <>
                  <span className="font-medium text-slate-700">Affaire {affaire.number}</span>
                  {affaire.project_id && (
                    <Link to={`/project/${affaire.project_id}`} className="hover:text-slate-800 hover:underline">{affaire.project_name}</Link>
                  )}
                  {affaire.client_name && <span>{affaire.client_name}</span>}
                  {estimate
                    ? <Link to={`/tools/offres/${estimate.id}`} className="text-blue-700 hover:underline">Chiffrage : {estimate.title}</Link>
                    : <span className="text-amber-700">Aucun chiffrage lié</span>}
                </>
              )}
              {!canEdit && (
                <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">
                  <Lock className="h-3 w-3" /> Lecture seule
                </span>
              )}
              {archived && <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs text-amber-700">Archivée</span>}
              <SaveStatus doc={doc} />
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-1">
            {canEdit && !archived && (
              <>
                <ToolbarButton onClick={undo} disabled={!canUndo || !editable} title="Annuler (Ctrl+Z)">
                  <Undo2 className="h-4 w-4" /> Annuler
                </ToolbarButton>
                <ToolbarButton onClick={redo} disabled={!canRedo || !editable} title="Rétablir (Ctrl+Y)">
                  <Redo2 className="h-4 w-4" /> Rétablir
                </ToolbarButton>
              </>
            )}
            <ToolbarButton onClick={() => setPanel(panel === "revisions" ? null : "revisions")} active={panel === "revisions"} title="Historique des versions">
              <History className="h-4 w-4" /> Versions
            </ToolbarButton>
            <ToolbarButton onClick={() => setPanel(panel === "comments" ? null : "comments")} active={panel === "comments"}
              title="Commentaires sur les slides, que Nexus peut appliquer">
              <MessageSquare className="h-4 w-4" /> Commentaires
              {openComments.length > 0 && (
                <span className="rounded-full bg-amber-500 px-1.5 text-[11px] font-semibold text-white">{openComments.length}</span>
              )}
            </ToolbarButton>
            <ToolbarButton onClick={() => setPanel(panel === "edit" ? null : "edit")} active={panel === "edit"}
              title="Mise en page de la slide, réglages du bloc sélectionné, ajout de blocs">
              <SlidersHorizontal className="h-4 w-4" /> Édition
            </ToolbarButton>
            {editable && (
              <ToolbarButton onClick={askNexusForSlide} title="Demander une modification de cette slide à Nexus">
                <Sparkles className="h-4 w-4" /> Nexus
              </ToolbarButton>
            )}
            <ToolbarButton onClick={() => setPanel(panel === "settings" ? null : "settings")} active={panel === "settings"}
              title={isDeck ? "Nature du document, référence, logo, PDF sur le Drive, modèles" : "Chiffrage lié, référence, dates, conditions, modèle client"}>
              <Settings2 className="h-4 w-4" /> Réglages
            </ToolbarButton>
            {isDeck && (
              <ToolbarButton onClick={() => setPanel(panel === "guides" ? null : "guides")} active={panel === "guides"}
                title="Consignes du client, du programme et du projet, tenues par l'IA d'après vos retours">
                <ListChecks className="h-4 w-4" /> Consignes
              </ToolbarButton>
            )}
            {isDeck && visibleSlides.length > 0 && (
              <ToolbarButton onClick={() => window.open(`/presenter/presentations/${id}`, `deepflow-presentation-${id}`,
                "popup,width=1280,height=760")}
                title="Ouvrir la présentation dans une fenêtre à part, sans rien de Deepflow autour : c'est elle qu'on partage en visio (flèches pour avancer, F pour le plein écran)">
                <Presentation className="h-4 w-4" /> Présenter
              </ToolbarButton>
            )}
            {clientProfile && (
              <ToolbarButton onClick={() => setPanel(panel === "client" ? null : "client")} active={panel === "client"} title="Consignes du client">
                <BookUser className="h-4 w-4" /> Fiche client
              </ToolbarButton>
            )}
            <ToolbarButton onClick={exportPdf} disabled={exporting} title={isDeck ? "Télécharger le PDF" : "Télécharger le PDF à envoyer au client"}>
              {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}
              {exporting ? "Préparation du PDF…" : "PDF"}
            </ToolbarButton>
            {canEdit && (
              <ToolbarButton onClick={() => updateDocument({ archived: !archived })} title={archived ? "Désarchiver" : "Archiver"}>
                {archived ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
                {archived ? "Désarchiver" : "Archiver"}
              </ToolbarButton>
            )}
          </div>
        </div>
      </div>

      <SyncBanners doc={doc} noun={`La ${config.noun.toLowerCase()}`} />
      {notice && (
        <div className="flex flex-col gap-2 rounded-xl border border-sky-200 bg-sky-50 p-3 text-sm text-sky-900 sm:flex-row sm:items-center sm:justify-between">
          <span className="inline-flex items-center gap-2">
            <Sparkles className="h-4 w-4 shrink-0" />
            <span>
              {notice.who} a modifié {notice.count > 1 ? `${notice.count} slides` : "une slide"}
              {notice.summary ? ` : ${notice.summary}` : "."}
            </span>
          </span>
          <div className="flex shrink-0 gap-2">
            {Object.keys(changes).length > 0 && (
              <button type="button" className="rounded-full bg-white px-3 py-1 text-slate-800 shadow-sm"
                onClick={() => selectSlide(slides.find((item) => changes[item.id])?.id)}>Voir</button>
            )}
            {editable && canUndo && (
              <button type="button" className="rounded-full bg-white px-3 py-1 text-slate-800 shadow-sm" onClick={undo}>
                Annuler (Ctrl+Z)
              </button>
            )}
            <button type="button" aria-label="Fermer" className="rounded-full p-1 text-sky-700 hover:bg-sky-100"
              onClick={() => setNotice(null)}><X className="h-4 w-4" /></button>
          </div>
        </div>
      )}

      <div ref={workspaceRef} className="flex flex-col gap-4 xl:flex-row" style={workspaceHeight ? { height: workspaceHeight } : undefined}>
        {/* Slides : un clic ouvre la slide, rien d'autre (déplacer, dupliquer, supprimer : au-dessus de la slide). */}
        <nav ref={navRef} aria-label="Slides"
          className="flex shrink-0 gap-3 overflow-x-auto pb-2 xl:h-full xl:w-[13.5rem] xl:flex-col xl:overflow-y-auto xl:overflow-x-hidden xl:p-1 xl:pr-2">
          {slides.map((item, index) => (
            <div key={item.id} className="shrink-0">
              <button type="button" onClick={() => selectSlide(item.id)} aria-current={item.id === slideId ? "true" : undefined}
                aria-label={`Slide ${index + 1} : ${item.title || item.layout}`}
                className={`block w-40 overflow-hidden rounded-md border-2 bg-white text-left xl:w-48 ${
                  item.id === slideId ? "border-blue-500" : "border-slate-200 hover:border-slate-300"} ${item.hidden ? "opacity-40" : ""} ${
                  changes[item.id] ? "ring-2 ring-sky-400 ring-offset-1" : ""}`}>
                <ScaledSlide>
                  <SlideCanvas slide={item} meta={shown.meta} estimate={estimate} page={pages[item.id]} embeds={embeds}
                    deckTitle={proposal.title} />
                </ScaledSlide>
              </button>
              <div className="mt-0.5 flex items-center justify-between gap-1 text-[11px] text-slate-500">
                <span className="truncate">{pages[item.id] ?? "masquée"}</span>
                <span className="flex items-center gap-1">
                  {changes[item.id] && (
                    <span className="inline-flex items-center gap-0.5 rounded-full bg-sky-100 px-1.5 text-sky-800"
                      title="Modifiée depuis votre dernière visite">
                      <Sparkles className="h-3 w-3" /> modifiée
                    </span>
                  )}
                  {openBySlide[item.id] > 0 && (
                    <span className="inline-flex items-center gap-0.5 rounded-full bg-amber-100 px-1.5 text-amber-800"
                      title="Commentaires ouverts">
                      <MessageSquare className="h-3 w-3" /> {openBySlide[item.id]}
                    </span>
                  )}
                  {item.hidden && <EyeOff className="h-3 w-3" />}
                </span>
              </div>
            </div>
          ))}
          {editable && (
            <div className="shrink-0">
              {addingSlide ? (
                <div className="w-40 space-y-1 rounded-md border border-slate-200 bg-white p-2 xl:w-48">
                  {layoutsFor(config.document).map((item) => (
                    <button key={item.layout} type="button" onClick={() => addSlide(item.layout)}
                      className="block w-full rounded px-2 py-1 text-left text-sm text-slate-700 hover:bg-slate-100">{item.label}</button>
                  ))}
                  <button type="button" onClick={() => setAddingSlide(false)} className="px-2 text-xs text-slate-500">Annuler</button>
                </div>
              ) : (
                <button type="button" onClick={() => setAddingSlide(true)}
                  className="inline-flex w-40 items-center justify-center gap-1.5 rounded-md border border-dashed border-slate-300 py-3 text-sm text-slate-600 hover:bg-white xl:w-48">
                  <Plus className="h-4 w-4" /> Slide
                </button>
              )}
            </div>
          )}
        </nav>

        {/* Slide en cours */}
        <main className="flex min-w-0 flex-1 flex-col gap-2">
          {slide ? (
            <>
              <div className="flex shrink-0 flex-wrap items-center justify-between gap-2">
                <span className="px-1 text-sm text-slate-500">
                  Slide {pages[slide.id] ?? "masquée"} sur {slides.filter((item) => !item.hidden).length}
                </span>
                <div className="flex items-center gap-0.5">
                  {editable && (
                    <>
                      <SlideButton title="Monter la slide" disabled={slideIndex === 0} onClick={() => apply((c) => moveSlide(c, slideId, -1))}>
                        <ArrowUp className="h-4 w-4" />
                      </SlideButton>
                      <SlideButton title="Descendre la slide" disabled={slideIndex === slides.length - 1}
                        onClick={() => apply((c) => moveSlide(c, slideId, 1))}>
                        <ArrowDown className="h-4 w-4" />
                      </SlideButton>
                      <SlideButton title="Dupliquer la slide" onClick={() => apply((c) => duplicateSlide(c, slideId))}>
                        <Copy className="h-4 w-4" />
                      </SlideButton>
                      <SlideButton title="Supprimer la slide" danger onClick={() => deleteSlide(slideId)}>
                        <Trash2 className="h-4 w-4" />
                      </SlideButton>
                      <span className="mx-1 h-5 w-px bg-slate-200" />
                    </>
                  )}
                  <SlideButton title="Voir la slide en grand (Échap pour revenir)" onClick={() => setEnlarged(true)}>
                    <Maximize2 className="h-4 w-4" />
                  </SlideButton>
                </div>
              </div>
              <div className="min-h-0 flex-1">
                {workspaceHeight ? (
                  <ScaledSlide fit className="rounded-lg bg-white shadow-sm ring-1 ring-slate-200">{canvas(true)}</ScaledSlide>
                ) : (
                  <div className="overflow-hidden rounded-lg border border-slate-200 shadow-sm">
                    <ScaledSlide>{canvas(true)}</ScaledSlide>
                  </div>
                )}
              </div>
            </>
          ) : (
            <p className="py-10 text-center text-sm text-slate-500">Aucune slide.</p>
          )}
        </main>

        {/* Panneau latéral */}
        {panel && (
        <div className="shrink-0 xl:h-full xl:overflow-y-auto">
        {panel === "revisions" && (
          <RevisionsPanel revisions={revisions} canEdit={canEdit && !archived} viewingId={viewing?.revision?.id}
            onView={viewRevision} onRestore={restoreRevision} onLabel={labelRevision} onClose={() => setPanel(null)}
            clientShare={clientShare} />
        )}
        {panel === "settings" && isDeck && (
          <PresentationSettings id={id} data={data} meta={shown.meta} editable={editable} onMeta={onMeta} drive={drive}
            onTemplateSaved={(template) => setData((previous) => ({ ...previous, template }))}
            onCategory={(category) => updateDocument({ category }).then(() => drive.refresh())}
            onClientLogo={(logo) => setData((previous) => ({ ...previous, client_logo: logo }))}
            onError={setMessage} onClose={() => setPanel(null)} />
        )}
        {panel === "guides" && isDeck && (
          <GuidesPanel id={id} scope={scope} editable={canEdit} onError={setMessage} onClose={() => setPanel(null)} />
        )}
        {panel === "settings" && !isDeck && (
          <ProposalSettings meta={shown.meta} editable={editable} estimates={data.estimates || []}
            estimateId={proposal.estimate_id} onEstimate={(estimateId) => updateDocument({ estimate_id: estimateId })}
            onMeta={onMeta} clientProfile={clientProfile} onTemplate={saveTemplate} onClientLogo={saveClientLogo}
            onClose={() => setPanel(null)} />
        )}
        {panel === "comments" && (
          <CommentsPanel proposalId={id} api={config.comments} comments={comments} refresh={refreshComments} slides={slides} pages={pages}
            currentSlide={slide} selectedBlockId={selectedBlockId} canEdit={canEdit} userEmployeeId={user?.employee_id}
            onGo={goTo} onAskNexus={askNexusForComments} onClose={() => setPanel(null)} onError={setMessage} />
        )}
        {panel === "client" && clientProfile && (
          <ClientProfilePanel profile={clientProfile} canEdit={canEdit} onClose={() => setPanel(null)}
            onSaved={(saved) => setData((previous) => ({ ...previous, client_profile: saved }))} />
        )}
        {panel === "edit" && slide && (
          <aside className="w-full shrink-0 rounded-xl border border-slate-200 bg-white p-4 shadow-sm xl:w-72">
            <SlideInspector
              slide={slide} block={block} lots={lots} editable={editable} document={config.document}
              onSlide={onSlide}
              onBlock={(patch) => onBlock(block.id, patch)}
              onAddBlock={(created) => { apply((c) => addBlock(c, slideId, created, selectedBlockId)); setSelectedBlockId(created.id); }}
              onMoveBlock={(delta) => apply((c) => moveBlock(c, slideId, block.id, delta))}
              onDuplicateBlock={() => apply((c) => duplicateBlock(c, slideId, block.id))}
              onRemoveBlock={() => { apply((c) => removeBlock(c, slideId, block.id)); setSelectedBlockId(null); }}
              onDeselect={() => setSelectedBlockId(null)}
            />
          </aside>
        )}
        </div>
        )}
      </div>

      {enlarged && slide && (
        <SlideViewer slides={slides} slideId={slideId} pages={pages} onSelect={selectSlide} onClose={() => setEnlarged(false)}>
          {canvas(false)}
        </SlideViewer>
      )}

    </div>
  );
};

export default ProposalEditor;
